import { Grid, makeStyles } from "@material-ui/core";
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import MaintainenenceTableDataGrid from "./MaintainenenceTableDataGrid";
import * as d3 from "d3";
import {
  selectTeamQuotaData,
  selectUpdatedRecords,
  clearUpdateQuotaData,
} from "./redux/management.slice";
import { bulkUpsertTeamQuotaData } from "./redux/management.thunk";
import LoadingData from "./LoadingData";
import { _isNaN } from "../../util/customFunctions";
import { store } from "../../util/store";

const useStyle = makeStyles((theme) => ({
  tableHeader: {
    fontSize: "0.85rem",
    background: "#4472c4",
    paddingRight: "10px",
    fontFamily: "Roboto/Helvetica/Aria/sans-serif",
    width: "7rem",
    fontWeight: 600,
    color: "white",
  },
  tableCell: {
    fontSize: "1rem",
    whiteSpace: "normal",
    fontFamily: "Roboto/Helvetica/Aria/sans-serif",
    fontSize: "0.85rem",
    fontWeight: 400,
    width: "7rem",
  },
}));

const QuotaTabPanel = ({
  data,
  setTouched,
  tab,
  handleSubmit,
  udpatedRecords,
  columnDefs,
  domLayout,
  gridStyle = { margin: "1rem 0rem" },
}) => {
  if (data === null) {
    return <LoadingData />;
  }

  return (
    <Grid container item justifyContent="center" style={gridStyle}>
      <MaintainenenceTableDataGrid
        setTouched={setTouched}
        tab={tab}
        data={data}
        handleSubmit={handleSubmit}
        udpatedRecords={udpatedRecords}
        domLayout={domLayout}
        columnDefs={columnDefs}
      />
    </Grid>
  );
};

const TeamQuotaPanel = ({ isDryRun }) => {
  const state = store.getState();
  const Currency_Symbol = state.app.user.Currency;
  const classes = useStyle();
  const [touched, setTouched] = useState(false);
  const dispatch = useDispatch();
  const teamQuota = useSelector(selectTeamQuotaData);
  const udpatedRecords = useSelector(selectUpdatedRecords);

  useEffect(() => {
    return () => {
      dispatch(clearUpdateQuotaData());
    };
  }, [dispatch]);

  const handleTeamSubmit = (updatedRecords) => {
    // Transform updatedRecords into bulk upsert format
    // Group by Team to create one object per team
    const teamMap = {};

    updatedRecords.forEach(record => {
      const team = record.Team;
      if (!teamMap[team]) {
        teamMap[team] = {
          quotaAssignments: []
        };
      }

      teamMap[team].quotaAssignments.push({
        team: record.Team,
        fiscalQuarter: record.FiscalQuarter,
        quota: Math.round(Number(record.Quota || record.newQuota)),
        NQP_Goal: Math.round(Number(record.NQP_GOAL || record.newNQP_GOAL)),
        canUpsert: true  // Set to true to allow upsert
      });
    });

    const bulkTeamQuotaData = Object.values(teamMap);
    dispatch(bulkUpsertTeamQuotaData({ bulkTeamQuotaData }));
  };

  const editBackgroundQuota = { "edit-bg": (params) => params.data.Quota_Updated };
  const editBackgroundNQP_GOAL = { "edit-bg": (params) => params.data.NQP_GOAL_Updated };

  const teamColumnDefs = [
    { field: "Team", sortable: true, filter: true, cellStyle: { textAlign: "left" }, cellClass: classes.tableCell, headerClass: classes.tableHeader, flex: 2 },
    { field: "FiscalQuarter", sortable: true, filter: true, type: "rightAligned", flex: 1.3, headerClass: classes.tableHeader },
    { field: "Quota", sortable: true, filter: true, type: "rightAligned", flex: 1.3, headerClass: classes.tableHeader, editable: true, valueSetter: (params) => { if (_isNaN(params.newValue) || parseInt(params.newValue) < 0 || params.newValue === "") { window.alert("Quota must be a valid positive number"); return false; } params.data.Quota = params.newValue; return true; }, cellClassRules: editBackgroundQuota, valueFormatter: (x) => `${Currency_Symbol} ${d3.format(",")(Math.round(x.value))}` },
    { field: "NQP_GOAL", headerName: "NQP Goal", sortable: true, filter: true, tooltipField: "NQP_GOAL", type: "rightAligned", flex: 1.3, headerClass: classes.tableHeader, editable: true, valueSetter: (params) => { if (_isNaN(params.newValue) || parseInt(params.newValue) < 0) { window.alert("NQP goal must be a positive number"); return false; } params.data.NQP_GOAL = params.newValue; return true; }, cellClassRules: editBackgroundNQP_GOAL, resizable: false, valueFormatter: (x) => `${Currency_Symbol} ${d3.format(",")(Math.round(x.value))}` },
  ];

  return (
    <QuotaTabPanel
      data={teamQuota}
      setTouched={setTouched}
      tab="team"
      handleSubmit={handleTeamSubmit}
      udpatedRecords={udpatedRecords}
      columnDefs={teamColumnDefs}
      gridStyle={{ margin: "1rem 0.25rem" }}
    />
  );
};

export default TeamQuotaPanel;
